import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { MOOD_TYPES } from '../data/mockData';

const Composer = ({ onSave }) => {
  const [selectedMood, setSelectedMood] = useState(null);
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    if (!selectedMood && !content) return;
    setIsSaving(true);
    
    // Simulate network request
    setTimeout(() => {
      setIsSaving(false);
      setSaved(true);
      if (onSave) {
        onSave({
          id: Date.now(),
          date: new Date().toISOString(),
          mood: selectedMood || 'neutral',
          content: content,
          tags: []
        });
      }
      
      // Reset after showing success
      setTimeout(() => {
        setSaved(false);
        setSelectedMood(null);
        setContent('');
      }, 2000);
    }, 800);
  };

  return (
    <div className="glass-panel rounded-2xl p-6 relative overflow-hidden">
      <AnimatePresence>
        {saved && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-ink-800/90 backdrop-blur-md z-10 flex flex-col items-center justify-center rounded-2xl"
          >
            <div className="w-16 h-16 rounded-full bg-aurora/20 flex items-center justify-center mb-4">
              <SafeIcon name="Check" className="w-8 h-8 text-aurora" />
            </div>
            <h3 className="text-lg font-medium text-white">Entry Encrypted & Saved</h3>
            <p className="text-sm text-gray-400 mt-1">Your reflection is safely stored.</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
          <SafeIcon name="Edit3" className="w-5 h-5 text-lilac" />
          Daily Check-in
        </h2>
        <span className="text-xs text-gray-500 font-mono">
          {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
        </span>
      </div>

      {/* Mood Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-400 mb-3">How's the frequency today?</label>
        <div className="flex flex-wrap gap-2 md:gap-3">
          {MOOD_TYPES.map((mood) => {
            const isSelected = selectedMood === mood.id;
            return (
              <button
                key={mood.id}
                onClick={() => setSelectedMood(mood.id)}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-300 flex-1 min-w-[70px] border ${
                  isSelected 
                    ? `border-${mood.color.split('-')[1]} ${mood.bgColor} shadow-[0_0_15px_rgba(var(--tw-colors-aurora),0.1)]` 
                    : 'border-ink-600 bg-ink-700/50 hover:bg-ink-600'
                }`}
              >
                <SafeIcon 
                  name={mood.icon} 
                  className={`w-6 h-6 ${isSelected ? mood.color : 'text-gray-500'}`} 
                />
                <span className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-gray-500'}`}>
                  {mood.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Text Area */}
      <div className="mb-6">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Brain dump... what's taking up your mental RAM?"
          className="w-full bg-ink-900/50 border border-ink-600 rounded-xl p-4 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-lilac/50 focus:ring-1 focus:ring-lilac/50 transition-all resize-none h-32"
        />
        
        {/* Reflection Prompts Suggestion */}
        {!content && (
          <div className="mt-3 flex flex-wrap gap-2">
            {["What's the biggest bottleneck?", "Did you delegate enough today?", "Name one tiny win."].map((prompt, i) => (
              <button 
                key={i}
                onClick={() => setContent(prompt + "\n")}
                className="text-xs bg-ink-700 hover:bg-ink-600 text-gray-400 py-1.5 px-3 rounded-full transition-colors border border-ink-600"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer Controls */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button className="p-2 rounded-lg text-gray-400 hover:text-aurora hover:bg-ink-700 transition-colors">
            <SafeIcon name="Hash" className="w-5 h-5" />
          </button>
          <button className="p-2 rounded-lg text-gray-400 hover:text-lilac hover:bg-ink-700 transition-colors">
            <SafeIcon name="Mic" className="w-5 h-5" />
          </button>
        </div>
        
        <button
          onClick={handleSave}
          disabled={!selectedMood && !content.trim() || isSaving}
          className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2
            ${(!selectedMood && !content.trim()) 
              ? 'bg-ink-700 text-gray-500 cursor-not-allowed' 
              : 'bg-gradient-to-r from-aurora/80 to-aurora text-ink-900 hover:shadow-[0_0_20px_rgba(110,231,183,0.3)] shadow-[0_0_10px_rgba(110,231,183,0.1)]'
            }`}
        >
          {isSaving ? (
            <motion.div 
              animate={{ rotate: 360 }} 
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            >
              <SafeIcon name="Loader" className="w-4 h-4" />
            </motion.div>
          ) : (
            <SafeIcon name="Send" className="w-4 h-4" />
          )}
          {isSaving ? 'Encrypting...' : 'Log Entry'}
        </button>
      </div>
    </div>
  );
};

export default Composer;